import { IObservable, Subject } from '~/rage/utils';
import { IPhoneContactPresence, IPhoneDialog, IPhoneMessage } from '~/shared';
import { IRpcService } from '../RpcService';
import {
	IActiveDialogMessagesState,
	IMessagesService,
} from './messagesServiceTypes';

const initialActiveDialogMessagesState: IActiveDialogMessagesState = {
	messages: [],
	loading: false,
};

export class MessagesService implements IMessagesService {
	private readonly dialogs = new Subject<IPhoneDialog[]>([]);
	private readonly activeDialogMessages =
		new Subject<IActiveDialogMessagesState>(initialActiveDialogMessagesState);
	private readonly contactsPresence = new Subject<IPhoneContactPresence[]>([]);

	private currentDialogId: number | null = null;
	private readonly messagesCache = new Map<number, IPhoneMessage[]>();
	private readonly activeDialogId = new Subject<number | null>(null);
	private readonly unreadByDialog = new Map<number, number>();

	constructor(private readonly rpc: IRpcService) {
		this.rpc.register('messages:dialogs:update', (dialogs: IPhoneDialog[]) => {
			for (const dialog of dialogs) {
				if (!this.unreadByDialog.has(dialog.id)) {
					this.unreadByDialog.set(dialog.id, dialog.unreadCount ?? 0);
				}
			}

			const existing = this.dialogs.getValue();
			const merged = [...existing];
			for (const incoming of dialogs) {
				const idx = merged.findIndex((d) => d.id === incoming.id);
				if (idx >= 0) {
					merged[idx] = incoming;
				} else {
					merged.push(incoming);
				}
			}
			this.updateDialogsWithUnread(merged);
		});

		this.rpc.register(
			'messages:dialogs:newMessage',
			(data: { dialogId: number; message: IPhoneMessage }) => {
				this.handleNewMessage(data.dialogId, data.message);
			},
		);

		this.rpc.register(
			'messages:contacts:presence',
			(list: IPhoneContactPresence[]) => {
				const updated = [...this.contactsPresence.getValue()];
				for (const entry of list) {
					const i = updated.findIndex(
						(p) => p.phoneNumber === entry.phoneNumber,
					);
					if (i >= 0) {
						updated[i] = entry;
					} else {
						updated.push(entry);
					}
				}
				this.contactsPresence.next(updated);
			},
		);
	}

	getDialogs(): IObservable<IPhoneDialog[]> {
		return this.dialogs;
	}

	private updateDialogsWithUnread(dialogs: IPhoneDialog[]): void {
		const nextDialogs = dialogs.map((dialog) => ({
			...dialog,
			unreadCount:
				this.unreadByDialog.get(dialog.id) ?? dialog.unreadCount ?? 0,
		}));
		this.dialogs.next(nextDialogs);
	}

	setActiveDialogId(dialogId: number | null): void {
		this.currentDialogId = dialogId;
		this.activeDialogId.next(dialogId);

		if (dialogId === null) {
			this.activeDialogMessages.next(initialActiveDialogMessagesState);
			return;
		}

		this.unreadByDialog.set(dialogId, 0);
		const currentDialogs = this.dialogs.getValue();
		if (currentDialogs.length > 0) {
			this.updateDialogsWithUnread(currentDialogs);
		}

		const cached = this.messagesCache.get(dialogId);
		if (cached !== undefined) {
			this.activeDialogMessages.next({
				messages: cached,
				loading: false,
			});
			return;
		}

		this.activeDialogMessages.next({ messages: [], loading: true });

		this.rpc
			.invokeServer<IPhoneMessage[]>('phone:dialogs:getMessages', {
				dialogId,
			})
			.then((messages) => {
				this.messagesCache.set(dialogId, messages);

				if (this.currentDialogId === dialogId) {
					this.activeDialogMessages.next({
						messages,
						loading: false,
					});
				}
			})
			.catch((err) => {
				console.error('[MessagesService] getMessages failed', err);

				if (this.currentDialogId === dialogId) {
					this.activeDialogMessages.next({
						messages: [],
						loading: false,
					});
				}
			});
	}

	getActiveDialogMessages(): IObservable<IActiveDialogMessagesState> {
		return this.activeDialogMessages;
	}

	getActiveDialogId(): IObservable<number | null> {
		return this.activeDialogId;
	}

	getContactsPresence(): IObservable<IPhoneContactPresence[]> {
		return this.contactsPresence;
	}

	sendMessage(dialogId: number, text: string): Promise<void> {
		return this.rpc.invokeServer('phone:dialogs:sendMessage', {
			dialogId,
			text,
		});
	}

	createDialog(targetPhoneNumber: string, message: string): Promise<number> {
		return this.rpc.invokeServer('phone:dialogs:createDialog', {
			targetPhoneNumber,
			message,
		});
	}

	private handleNewMessage(dialogId: number, message: IPhoneMessage): void {
		const cached = this.messagesCache.get(dialogId) ?? [];
		const next = [...cached, message];

		this.messagesCache.set(dialogId, next);

		if (this.currentDialogId === dialogId) {
			this.activeDialogMessages.next({
				messages: next,
				loading: false,
			});
		}

		const isActive = this.currentDialogId === dialogId;
		const prevUnread = this.unreadByDialog.get(dialogId) ?? 0;
		this.unreadByDialog.set(dialogId, isActive ? 0 : prevUnread + 1);

		const currentDialogs = this.dialogs.getValue();
		const updatedDialogs = currentDialogs.map((dialog) =>
			dialog.id === dialogId
				? { ...dialog, lastMessage: message, updatedAt: message.createdAt }
				: dialog,
		);
		if (updatedDialogs.length > 0) {
			this.updateDialogsWithUnread(updatedDialogs);
		}
	}
}
